/**
 * EditingCanvas Component
 * Feature: 006-critique-editing (crop)
 * Feature: 011-light-editing (light adjustments, combined edits)
 *
 * Full-screen editing interface with crop and light tools.
 * Reusable component that can be integrated into critique workflow or other editing scenarios.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { Application } from 'pixi.js'
import { CropTool } from './composed/CropTool'
import { EditPreviewComparison } from './composed/EditPreview'
import { AdjustmentPanel } from './composed/AdjustmentPanel'
import { HistogramDisplay } from './composed/HistogramDisplay'
import { EditToolbar } from './composed/EditToolbar'
import { BaseEditSelector } from './BaseEditSelector'
import { PhotoEditorCanvasSizable } from './PhotoEditor/PhotoEditorCanvasSizable'
import { SplitPane, Panel } from './layout/SplitPane'
import { getVariantUrl } from './photos/AdaptivePhoto'
import type { CropData, CritiqueEdit, LightEditData, CombinedEditData } from '../types/edit.types'
import { DEFAULT_LIGHT_EDIT } from '../types/edit.types'
import logger from '../utils/logger'

type ToolType = 'crop' | 'light'

interface EditingCanvasProps {
  /** Photo to edit */
  photo: import('../types/photo').Photo
  /** Initial edit (for updating existing edit) */
  initialEdit?: CritiqueEdit
  /** Base edit to build upon (for edit chaining) */
  baseEdit?: CritiqueEdit
  /** Available edits for this photo (for base edit selection) */
  availableEdits?: CritiqueEdit[]
  /** Callback when save is clicked - now supports crop, light, or combined */
  onSave: (editType: 'crop' | 'light' | 'combined', editData: CropData | LightEditData | CombinedEditData, comment: string, baseEditId?: string) => void
  /** Callback when cancel is clicked */
  onCancel: () => void
  /** Whether save operation is in progress */
  isSaving?: boolean
}

/**
 * Full-screen editing canvas with crop tool
 *
 * @example
 * <EditingCanvas
 *   photo={photo}
 *   onSave={(cropData) => createEdit({ editType: 'crop', editData: cropData })}
 *   onCancel={() => setEditMode(false)}
 * />
 */
export function EditingCanvas({
  photo,
  initialEdit,
  baseEdit,
  availableEdits = [],
  onSave,
  onCancel,
  isSaving = false,
}: EditingCanvasProps): JSX.Element {
  // Active tool state (crop or light)
  const [activeTool, setActiveTool] = useState<ToolType>('crop')

  // Crop data state (preserved when switching tools)
  const [cropData, setCropData] = useState<CropData | undefined>(() => {
    if (initialEdit && initialEdit.editType === 'crop') {
      return initialEdit.editData as CropData
    }
    if (initialEdit && initialEdit.editType === 'combined') {
      return (initialEdit.editData as CombinedEditData).crop
    }
    return undefined
  })

  // Light data state (preserved when switching tools)
  const [lightData, setLightData] = useState<LightEditData>(() => {
    if (initialEdit && initialEdit.editType === 'light') {
      return initialEdit.editData as LightEditData
    }
    if (initialEdit && initialEdit.editType === 'combined') {
      return (initialEdit.editData as CombinedEditData).light ?? DEFAULT_LIGHT_EDIT
    }
    return DEFAULT_LIGHT_EDIT
  })

  const [showPreview, setShowPreview] = useState(false)
  const [selectedBaseEditId, setSelectedBaseEditId] = useState<string | null>(
    baseEdit?.id || null
  )

  // PixiJS app instance for histogram (feature 012-histogram-display)
  const [pixiApp, setPixiApp] = useState<Application | null>(null)

  // Image bounds for histogram pixel extraction (feature 012-histogram-display)
  const [imageBounds, setImageBounds] = useState<{ x: number; y: number; width: number; height: number } | null>(null)

  // Adjustment version counter to trigger histogram recalculation
  const adjustmentVersionRef = useRef(0)
  const [adjustmentVersion, setAdjustmentVersion] = useState(0)

  // Handle escape key to cancel
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onCancel])

  // Handle crop change
  const handleCropChange = useCallback((newCrop: CropData) => {
    setCropData(newCrop)
  }, [])

  // Handle light adjustment change (feature 012-histogram-display)
  const handleLightDataChange = useCallback((newLightData: LightEditData) => {
    setLightData(newLightData)
    // Increment adjustment version to trigger histogram recalculation
    adjustmentVersionRef.current += 1
    setAdjustmentVersion(adjustmentVersionRef.current)
  }, [])

  // Helper to check if light data is at defaults
  const isLightDataDefault = useCallback((light: LightEditData): boolean => {
    return Object.keys(DEFAULT_LIGHT_EDIT).every(
      (key) => light[key as keyof LightEditData] === DEFAULT_LIGHT_EDIT[key as keyof LightEditData]
    )
  }, [])

  // Handle save - detects and creates appropriate edit type
  // Note: Comment is handled by parent CommentForm (043-critique-edit-suggestions)
  const handleSave = useCallback(() => {
    const hasCrop = cropData !== undefined
    const hasLight = !isLightDataDefault(lightData)

    if (hasCrop && hasLight) {
      // Combined edit
      const combinedData: CombinedEditData = {
        crop: cropData,
        light: lightData,
      }
      onSave('combined', combinedData, '', selectedBaseEditId || undefined)
    } else if (hasCrop) {
      // Crop only
      onSave('crop', cropData, '', selectedBaseEditId || undefined)
    } else if (hasLight) {
      // Light only
      onSave('light', lightData, '', selectedBaseEditId || undefined)
    } else {
      // No changes - this shouldn't happen with proper UI validation
      logger.warn('EditingCanvas', 'No changes to save')
    }
  }, [cropData, lightData, selectedBaseEditId, onSave, isLightDataDefault])

  const canSave = cropData !== undefined || !isLightDataDefault(lightData)

  return (
    <div className="fixed inset-0 z-50 bg-background/95 flex flex-col">
      {/* Header */}
      <EditToolbar
        activeTool={activeTool}
        onToolChange={setActiveTool}
        onSave={handleSave}
        onCancel={onCancel}
        onPreviewToggle={() => setShowPreview(!showPreview)}
        showPreview={showPreview}
        canSave={canSave}
        isSaving={isSaving}
        title={initialEdit ? 'Edit Suggestion' : 'Suggest Edit'}
        subtitle={
          selectedBaseEditId
            ? 'Building on previous edit'
            : activeTool === 'crop'
            ? 'Define crop region for this photo'
            : 'Adjust light and tone'
        }
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto min-h-0">
        <div className="flex flex-col min-h-full">
          {/* Top Section: Base Edit Selector & Comment */}
          <div className="px-6 pt-6 space-y-4">
            {/* Base Edit Selector (for edit chaining) */}
            {availableEdits.length > 0 && !initialEdit && (
              <div className="bg-surface-2 rounded-lg p-4">
                <BaseEditSelector
                  edits={availableEdits}
                  selectedBaseEditId={selectedBaseEditId}
                  onSelectBaseEdit={setSelectedBaseEditId}
                />
              </div>
            )}
          </div>

          {!showPreview ? (
            /* Editing Mode */
            <div className="flex-1 px-6 py-4 min-h-0">
              {activeTool === 'crop' ? (
                /* Crop Tool */
                <div className="h-full flex items-center justify-center bg-surface-2 rounded-lg p-6 min-h-[500px]">
                  <CropTool
                    photo={photo}
                    initialCrop={cropData}
                    onChange={handleCropChange}
                    className="mx-auto max-w-full"
                  />
                </div>
              ) : (
                /* Light Tool - Resizable split pane layout */
                <div className="h-full">
                  <SplitPane direction="horizontal" autoSaveId="critique-light-edit">
                    {/* Canvas Area - Left panel */}
                    <Panel defaultSize={70} minSize={50}>
                      <div className="h-full bg-surface-2 rounded-lg flex items-center justify-center p-4">
                        <PhotoEditorCanvasSizable
                          id={photo.id}
                          imageUrl={getVariantUrl(photo, 'medium', true) || ''}
                          exposure={lightData.exposure}
                          contrast={lightData.contrast}
                          highlights={lightData.highlights}
                          shadows={lightData.shadows}
                          whites={lightData.whites}
                          blacks={lightData.blacks}
                          cropData={cropData} // Show crop overlay if it exists
                          originalImageWidth={photo.width ?? undefined}
                          originalImageHeight={photo.height ?? undefined}
                          onAppReady={setPixiApp}
                          onImageBoundsChange={setImageBounds}
                        />
                      </div>
                    </Panel>

                    {/* Adjustment Panel - Right panel (resizable) */}
                    <Panel defaultSize={30} minSize={20} maxSize={40}>
                      <div className="h-full bg-surface-2 rounded-lg p-4 overflow-y-auto space-y-4">
                        {/* Histogram Display */}
                        <HistogramDisplay
                          app={pixiApp}
                          imageBounds={imageBounds}
                          adjustmentVersion={adjustmentVersion}
                          exifData={photo.metadata as any}
                        />

                        {/* Light Adjustment Sliders */}
                        <AdjustmentPanel
                          values={lightData}
                          onChange={handleLightDataChange}
                        />
                      </div>
                    </Panel>
                  </SplitPane>
                </div>
              )}
            </div>
          ) : (
            /* Preview Mode */
            <div className="flex-1 px-6 py-4 flex flex-col">
              <div className="text-center mb-4">
                <h3 className="text-lg font-medium text-foreground mb-2">Preview Comparison</h3>
                <p className="text-sm text-muted">
                  See how your suggested {cropData && !isLightDataDefault(lightData) ? 'crop and light' : cropData ? 'crop' : 'light'} changes the image
                </p>
              </div>

              {(cropData || !isLightDataDefault(lightData)) && (
                <div className="flex-1 bg-surface-2 rounded-lg p-6">
                  <EditPreviewComparison
                    id={photo.id}
                    photoUrl={getVariantUrl(photo, 'medium', true) || ''}
                    photoTitle={photo.title}
                    cropData={cropData}
                    lightData={lightData}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer Help Text */}
      <div className="px-6 py-3 bg-surface-3 border-t border-border">
        <p className="text-xs text-muted">
          <span className="font-medium text-foreground">Tip:</span>
          {activeTool === 'crop'
            ? ' Drag the corners to adjust crop region. Use aspect ratio buttons to constrain proportions. Press Escape to cancel.'
            : ' Use sliders to adjust exposure, contrast, and tonal range. Changes apply in real-time. Press Escape to cancel.'
          }
        </p>
      </div>
    </div>
  )
}

export default EditingCanvas
